import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as api from '@/features/backlog/api';
import { MentionComment, Member } from '@/features/backlog/types';
import { renderCommentBody } from '@/features/backlog/mentions';
import { timeAgo } from '@/features/backlog/format';
import { ROUTES, taskPath } from '@/constants/routes';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';

/**
 * One "my comment" row: the item key chip + title (both link to the item, deep-linking to this exact
 * comment via `?hl=comment:<id>`), an optional board chip + relative time, then the comment body with
 * @mentions rendered. Shared by the notification-bell tab and the dedicated /me/comments page.
 */
export function MyCommentRow({
  c,
  onGo,
  memberById,
  meId,
  boardName,
}: {
  c: MentionComment;
  onGo: () => void;
  memberById: Map<string, Member>;
  meId: string;
  boardName?: string;
}) {
  return (
    <div className="px-2 py-2 transition-colors hover:bg-layer/40">
      <div className="flex items-center gap-2 text-[11px] text-fg-faint">
        <button
          type="button"
          onClick={onGo}
          className="item-key shrink-0 rounded px-1 py-px tnum text-[11px] font-semibold transition-opacity hover:opacity-80"
          title={c.item_title}
        >
          {c.item_key}
        </button>
        <button type="button" onClick={onGo} className="min-w-0 flex-1 truncate text-left text-fg-soft transition-colors hover:text-fg">
          {c.item_title || 'Untitled'}
        </button>
        {boardName ? <span className="hidden shrink-0 truncate rounded-full bg-layer-strong px-2 py-0.5 text-[10.5px] font-medium text-fg-faint sm:block">{boardName}</span> : null}
        <span className="shrink-0 whitespace-nowrap">{timeAgo(c.created_at)}</span>
      </div>
      <div className="mt-0.5 whitespace-pre-wrap pl-1 text-[12.5px] text-fg-soft">{renderCommentBody(c.body, memberById, meId)}</div>
    </div>
  );
}

/**
 * The "My comments" TAB body inside the notification bell — the caller's most recent comments (newest
 * first), each linking straight to the comment on its item, plus a "View all" link to the dedicated
 * paginated page. Modelled on MentionsPanel but read-only (no resolve/reply).
 */
export function MyCommentsPanel({ close }: { close: () => void }) {
  const { me } = useIdentity();
  const { members, boards } = useAppData();
  const navigate = useNavigate();
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const [list, setList] = useState<MentionComment[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!me) return;
    api
      .getMyComments(30, 0)
      .then((r) => { setList(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [me]);

  if (!me) return null;

  const goTo = (c: MentionComment) => {
    const b = boardById.get(c.board_id);
    if (b && c.item_key) navigate(taskPath(b.slug, c.item_key) + `?hl=comment:${c.id}`);
    close();
  };

  return (
    <div>
      {list.length === 0 ? (
        <div className="px-3 py-10 text-center text-[12px] text-fg-faint">{loaded ? "You haven't commented anywhere yet." : 'Loading…'}</div>
      ) : (
        list.map((c) => (
          <MyCommentRow key={c.id} c={c} onGo={() => goTo(c)} memberById={memberById} meId={me.id} boardName={boardById.get(c.board_id)?.name} />
        ))
      )}
      {list.length > 0 ? (
        <div className="mt-1 border-t border-line pt-1">
          <button
            type="button"
            onClick={() => { navigate(ROUTES.MY_COMMENTS); close(); }}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[12.5px] font-medium text-accent transition-colors hover:bg-layer"
          >
            View all my comments
          </button>
        </div>
      ) : null}
    </div>
  );
}
